/**
 * Admin Settings Page
 * Monitoring and statistics for AI indexing system + Email notifications
 */

import { useState, useEffect } from 'react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { getApiUrl } from '../../config/api';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  useGetGlobalEmailNotificationsQuery,
  useToggleGlobalEmailNotificationsMutation,
  useGetEmailNotificationStatsQuery,
} from '../../services/api';

interface EnrichmentStats {
  cve_enrichment: {
    total_cves: number;
    enriched_cves: number;
    critical_cves: number;
    high_cves: number;
  };
  version_enrichment: {
    total_versions: number;
    enriched_versions: number;
    security_updates: number;
    critical_patches: number;
  };
  page_types: Array<{ page_type: string; count: number }>;
  recent_activity: Array<{ date: string; pages_indexed: number }>;
}

interface IndexingHealth {
  status: string;
  issues: string[];
  metrics: {
    stale_pages: number;
    unenriched_versions: number;
    unenriched_cves: number;
    recent_indexing_24h: number;
  };
}

interface IndexedPage {
  id: number;
  url: string;
  page_type: string;
  indexed_at: string;
  last_queried_at: string | null;
  metadata: string;
}

export default function AdminSettings() {
  const [stats, setStats] = useState<EnrichmentStats | null>(null);
  const [health, setHealth] = useState<IndexingHealth | null>(null);
  const [indexedPages, setIndexedPages] = useState<IndexedPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Email notification state
  const { data: globalEmailSettings } = useGetGlobalEmailNotificationsQuery();
  const { data: emailStats } = useGetEmailNotificationStatsQuery(undefined, {
    pollingInterval: 30000, // Refresh every 30 seconds
  });
  const [toggleGlobalEmails, { isLoading: isTogglingEmail }] = useToggleGlobalEmailNotificationsMutation();
  const [globalEmailEnabled, setGlobalEmailEnabled] = useState(false);

  useEffect(() => {
    if (globalEmailSettings?.data?.enabled !== undefined) {
      setGlobalEmailEnabled(globalEmailSettings.data.enabled);
    }
  }, [globalEmailSettings]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load stats, health, and indexed pages in parallel
      const [statsRes, healthRes, pagesRes] = await Promise.all([
        fetchWithAuth(getApiUrl('api/v1/admin/enrichment-stats')),
        fetchWithAuth(getApiUrl('api/v1/admin/indexing-health')),
        fetchWithAuth(getApiUrl(`api/v1/admin/indexed-pages?page=${currentPage}&limit=20`)),
      ]);

      const statsData = await statsRes.json();
      const healthData = await healthRes.json();
      const pagesData = await pagesRes.json();

      if (statsData.success) setStats(statsData.data);
      if (healthData.success) setHealth(healthData.data);
      if (pagesData.success) {
        setIndexedPages(pagesData.data);
        setTotalPages(pagesData.pagination.total_pages);
      }
    } catch (error) {
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [currentPage]);

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      loadData();
      return;
    }

    try {
      const res = await fetchWithAuth(getApiUrl(`api/v1/admin/search-indexed/${encodeURIComponent(searchQuery)}`));
      const data = await res.json();
      if (data.success) {
        setIndexedPages(data.data);
      }
    } catch (error) {
      console.error('Error searching:', error);
    }
  };

  const handleToggleGlobalEmails = async (enabled: boolean) => {
    setGlobalEmailEnabled(enabled);
    try {
      await toggleGlobalEmails(enabled).unwrap();
    } catch (error) {
      console.error('Failed to update global email settings:', error);
      // Revert on error
      setGlobalEmailEnabled(!enabled);
    }
  };

  const formatEmailDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'text-green-400 bg-green-900/20 border-green-500/30';
      case 'degraded':
        return 'text-yellow-400 bg-yellow-900/20 border-yellow-500/30';
      case 'warning':
        return 'text-orange-400 bg-orange-900/20 border-orange-500/30';
      default:
        return 'text-gray-400 bg-gray-800/30 border-gray-700';
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-100">Admin Settings</h1>
          <p className="text-gray-400 mt-1">System Configuration & Monitoring</p>
        </div>
        <button
          onClick={loadData}
          className="flex items-center gap-2 px-4 py-2 bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/30 rounded hover:bg-cyber-blue-500/20 transition-colors"
        >
          <ArrowPathIcon className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Email Notification System */}
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-100 mb-4">Email Notification System</h2>

        <div className="space-y-6">
          <div className="border border-gray-700 rounded-lg p-4 bg-gray-900/30">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={globalEmailEnabled}
                onChange={(e) => handleToggleGlobalEmails(e.target.checked)}
                disabled={isTogglingEmail}
                className="mt-1 w-5 h-5 rounded border-gray-600 bg-gray-900 text-cyber-blue-500 focus:ring-2 focus:ring-cyber-blue-500 focus:ring-offset-0 disabled:opacity-50"
              />
              <div className="flex-1">
                <div className="font-semibold text-lg text-gray-100">Enable Global Email Notifications</div>
                <div className="text-sm text-gray-400 mt-1">
                  When disabled, NO users will receive email notifications regardless of their subscription status.
                  Use this as an emergency kill switch.
                </div>
                {!globalEmailEnabled && (
                  <div className="mt-2 px-3 py-2 bg-red-900/20 border border-red-700 rounded text-red-400 text-sm">
                    ⚠️ Email notifications are currently DISABLED globally
                  </div>
                )}
              </div>
            </label>
          </div>

          {emailStats?.data && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="border border-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-400">Subscribed Users</div>
                <div className="text-2xl font-bold text-cyber-blue-400 mt-1">
                  {emailStats.data.subscribedUsers}
                </div>
              </div>

              <div className="border border-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-400">Active Users</div>
                <div className="text-2xl font-bold text-green-400 mt-1">
                  {emailStats.data.activeUsers}
                </div>
              </div>

              <div className="border border-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-400">Last Digest Sent</div>
                <div className="text-sm font-semibold mt-1 text-gray-200">
                  {formatEmailDate(emailStats.data.lastDigestTime)}
                </div>
              </div>

              <div className="border border-gray-700 rounded-lg p-4">
                <div className="text-sm text-gray-400">Emails Sent Today</div>
                <div className="text-2xl font-bold text-yellow-400 mt-1">
                  {emailStats.data.emailsSentToday}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Indexing System Header */}
      <div className="pt-4">
        <h2 className="text-2xl font-bold text-gray-100">AI Indexing System</h2>
        <p className="text-gray-400 mt-1">Monitor and manage the AI-powered enrichment system</p>
      </div>

      {/* Health Status */}
      {health && (
        <div className={`rounded-lg border p-6 ${getHealthColor(health.status)}`}>
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold mb-2 flex items-center gap-2">
                {health.status === 'healthy' ? (
                  <CheckCircleIcon className="w-6 h-6" />
                ) : (
                  <ExclamationTriangleIcon className="w-6 h-6" />
                )}
                System Health: {health.status.charAt(0).toUpperCase() + health.status.slice(1)}
              </h2>
              {health.issues.length > 0 && (
                <ul className="space-y-1 text-sm opacity-90">
                  {health.issues.map((issue, idx) => (
                    <li key={idx}>• {issue}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <div className="opacity-70">Indexed (24h)</div>
                <div className="text-2xl font-bold">{health.metrics.recent_indexing_24h}</div>
              </div>
              <div>
                <div className="opacity-70">Pending CVEs</div>
                <div className="text-2xl font-bold">{health.metrics.unenriched_cves}</div>
              </div>
              <div>
                <div className="opacity-70">Pending Versions</div>
                <div className="text-2xl font-bold">{health.metrics.unenriched_versions}</div>
              </div>
              <div>
                <div className="opacity-70">Stale Pages</div>
                <div className="text-2xl font-bold">{health.metrics.stale_pages}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* CVE Enrichment Stats */}
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-100 mb-4">CVE Enrichment</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Total CVEs:</span>
                <span className="text-gray-100 font-semibold">{stats.cve_enrichment.total_cves}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Enriched:</span>
                <span className="text-green-400 font-semibold">
                  {stats.cve_enrichment.enriched_cves}
                  <span className="text-sm ml-2 text-gray-500">
                    ({((stats.cve_enrichment.enriched_cves / stats.cve_enrichment.total_cves) * 100).toFixed(1)}%)
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Critical:</span>
                <span className="text-red-400 font-semibold">{stats.cve_enrichment.critical_cves}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">High:</span>
                <span className="text-orange-400 font-semibold">{stats.cve_enrichment.high_cves}</span>
              </div>
            </div>
          </div>

          {/* Version Enrichment Stats */}
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-100 mb-4">Version Enrichment</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-400">Total Versions:</span>
                <span className="text-gray-100 font-semibold">{stats.version_enrichment.total_versions}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Enriched:</span>
                <span className="text-green-400 font-semibold">
                  {stats.version_enrichment.enriched_versions}
                  <span className="text-sm ml-2 text-gray-500">
                    ({((stats.version_enrichment.enriched_versions / stats.version_enrichment.total_versions) * 100).toFixed(1)}%)
                  </span>
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Security Updates:</span>
                <span className="text-red-400 font-semibold">{stats.version_enrichment.security_updates}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Critical Patches:</span>
                <span className="text-orange-400 font-semibold">{stats.version_enrichment.critical_patches}</span>
              </div>
            </div>
          </div>

          {/* Page Types */}
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-100 mb-4">Indexed Content Types</h3>
            <div className="space-y-2">
              {stats.page_types.map((type) => (
                <div key={type.page_type} className="flex justify-between items-center">
                  <span className="text-gray-400 capitalize">{type.page_type.replace('_', ' ')}:</span>
                  <span className="text-gray-100 font-semibold">{type.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-gray-100 mb-4">Recent Activity (7 days)</h3>
            <div className="space-y-2">
              {stats.recent_activity.map((day) => (
                <div key={day.date} className="flex justify-between items-center">
                  <span className="text-gray-400">{new Date(day.date).toLocaleDateString()}:</span>
                  <span className="text-gray-100 font-semibold">{day.pages_indexed} pages</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search & Indexed Pages */}
      <div className="bg-gray-800/30 border border-gray-700 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-100">Indexed Pages</h3>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search by URL or CVE..."
              className="px-3 py-1.5 bg-gray-900/50 border border-gray-700 rounded text-sm text-gray-100 focus:outline-none focus:border-cyber-blue-500"
            />
            <button
              onClick={handleSearch}
              className="p-1.5 bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/30 rounded hover:bg-cyber-blue-500/20 transition-colors"
            >
              <MagnifyingGlassIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-700">
                <th className="pb-2 font-medium">Type</th>
                <th className="pb-2 font-medium">URL</th>
                <th className="pb-2 font-medium">Indexed At</th>
                <th className="pb-2 font-medium">Metadata</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {indexedPages.map((page) => (
                <tr key={page.id} className="hover:bg-gray-800/30">
                  <td className="py-2">
                    <span className="text-xs px-2 py-1 bg-cyber-blue-500/10 text-cyber-blue-400 border border-cyber-blue-500/30 rounded">
                      {page.page_type}
                    </span>
                  </td>
                  <td className="py-2">
                    <a
                      href={page.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyber-blue-400 hover:text-cyber-blue-300 truncate max-w-md block"
                    >
                      {page.url}
                    </a>
                  </td>
                  <td className="py-2 text-gray-400">
                    {new Date(page.indexed_at).toLocaleString()}
                  </td>
                  <td className="py-2 text-gray-400 text-xs max-w-xs truncate">
                    {page.metadata && JSON.parse(page.metadata).cve_id && (
                      <span className="text-yellow-400">CVE: {JSON.parse(page.metadata).cve_id}</span>
                    )}
                    {page.metadata && JSON.parse(page.metadata).version_id && (
                      <span className="text-green-400">Version ID: {JSON.parse(page.metadata).version_id}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
          <span className="text-sm text-gray-400">
            Page {currentPage} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 bg-gray-700 text-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
